import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Url } from 'src/api';
import Dialog, { DialogActionBar } from 'src/components/common/Dialog.redesign';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import County from 'src/models/County';
import ROWLine from 'src/models/ROWLine';
import { IRootState } from 'src/reducers';
import useFetch from 'use-http';

interface IAddUpdateROWLineDialogProps {
    rowLine?: ROWLine;
    counties?: Array<County>;
    onUpdate: () => void;

    onCloseDialog?: () => void;
}

const AddUpdateROWLineDialog: React.FC<IAddUpdateROWLineDialogProps> = (props) => {
    const appContext = useSelector((state: IRootState) => state.appContext);
    const { reset, register, errors, handleSubmit } = useForm();
    const [selectedCounties, setSelectedCounties] = useState<County[]>([]);
    const { get, put, post, response } = useFetch();
    const [title, setTitle] = useState<string>('');
    const [availableCounties, setAvailableCounties] = useState<County[]>([]);
    const [dropdownSearch, setDropdownSearch] = useState<string>('');
    const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        getCounties();

        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
                setDropdownSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (props.rowLine !== undefined && props.rowLine?.id > 0) {
            setTitle('Edit Right Of Way Line');
            reset(props.rowLine);
            setSelectedCounties(props.rowLine.counties ?? []);
        } else {
            setTitle('Create Right Of Way Line');
            setSelectedCounties([]);
        }
    }, [props.rowLine]);

    const getCounties = async () => {
        const countiesResult = await get(Url.project.getProjectCounties(appContext));

        if (response.ok) {
            setAvailableCounties(countiesResult.items);
        }
    };

    const addCounty = (county: County) => {
        setSelectedCounties((prev) => prev.some((c) => c.id === county.id) ? prev : [...prev, county]);
        setDropdownOpen(false);
        setDropdownSearch('');
    };

    const removeCounty = (countyId: number) => {
        setSelectedCounties((prev) => prev.filter((c) => c.id !== countyId));
    };

    const createROWLine = async (data: ROWLine) => {
        await post(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/row-lines`, data);

        if (response.ok) {
            props.onUpdate();
            props.onCloseDialog();
        }    
    };

    const updateROWLine = async (data: ROWLine) => {
        await put(`v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/row-lines`, data);

        if (response.ok) {
            props.onUpdate();
            props.onCloseDialog();
        }    
    };

    const onSubmit = async (data: ROWLine) => {
        data = finalizeData(data);

        if (data.id > 0) {
            updateROWLine(data);
        } else {
            createROWLine(data);
        }
    };

    const finalizeData = (data: ROWLine) => {
        // metadata
        data.id = props.rowLine?.id ?? 0;
        data.lineNumber = props.rowLine?.lineNumber ?? null;

        // integers
        data.lineId = data.lineId ? parseInt(data.lineId + '', 10) : null;

        // floats
        data.lineLength = data.lineLength ? parseFloat(data.lineLength + '') : null;

        // counties: diff against original to produce add/delete lists
        const originalCounties = props.rowLine?.counties ?? [];
        data.counties = selectedCounties;
        data.addCounties = selectedCounties.filter((c) => !originalCounties.some((o) => o.id === c.id));
        data.deleteCounties = originalCounties.filter((o) => !selectedCounties.some((c) => c.id === o.id));

        return data;
    };

    return (
        <Dialog title={title}>
            <form onSubmit={handleSubmit((data) => onSubmit(data as unknown as ROWLine))}>
                <FormRow style={{ padding: '1rem' }}>
                    <RHFInputBox bodyClassName="lh-two-fifths-width" style={{ width: '100%' }} label="Name" name="name" register={register} errors={errors} />
                    <RHFInputBox bodyClassName="lh-fifth-width" style={{ width: '100%' }} label="Line ID" name="lineId" type="number" register={register} errors={errors} />
                    <RHFInputBox bodyClassName="lh-two-fifths-width" style={{ width: '100%' }} label="Line Length (ft)" name="lineLength" type="number" register={register} errors={errors} />
                </FormRow>
                <FormRow style={{ padding: '1rem' }}>
                    <RHFInputBox bodyClassName="lh-two-fifths-width" style={{ width: '100%' }} label="POB Parcel Id" name="pobParcelId" register={register} errors={errors} />
                    <RHFInputBox bodyClassName="lh-three-fifths-width" style={{ width: '100%' }} label="POB Parcel Name" name="pobParcelName" register={register} errors={errors} />
                </FormRow>
                <FormRow style={{ padding: '1rem' }}>
                    <RHFInputBox bodyClassName="lh-two-fifths-width" style={{ width: '100%' }} label="POT Parcel Id" name="potParcelId" register={register} errors={errors} />
                    <RHFInputBox bodyClassName="lh-three-fifths-width" style={{ width: '100%' }} label="POT Parcel Name" name="potParcelName" register={register} errors={errors} />
                </FormRow>
                <FormRow style={{ padding: '1rem' }}>
                    <div className="dlc-form-tag-select-wrapper" style={{ width: '100%' }}>
                        <div style={{ background: 'white', display: 'flex', flexWrap: 'wrap', padding: '11px', minHeight: '4.8rem' }}>
                            {selectedCounties.map((county) => (
                                <span
                                    key={county.id}
                                    style={{ padding: '.6rem', border: '1px solid #ccc', background: 'white', margin: '0px 1px', display: 'inline-block' }}
                                >
                                    {county.name}
                                    <span onClick={() => removeCounty(county.id)} style={{ cursor: 'pointer', padding: '0 0 0 3px' }}>
                                        <i className="fas fa-times-circle" />
                                    </span>
                                </span>
                            ))}
                            <div ref={dropdownRef} style={{ position: 'relative' }}>
                                <div className="dlc-tag-select">
                                    <input
                                        className="dlc-tag-select-input"
                                        style={{ height: '3rem' }}
                                        value={dropdownSearch}
                                        onChange={(e) => { setDropdownSearch(e.target.value); setDropdownOpen(true); }}
                                        onClick={() => setDropdownOpen(true)}
                                        placeholder=""
                                    />
                                    <div className={`dlc-tag-select-dropdown ${dropdownOpen ? 'showing' : ''}`}>
                                        {dropdownOpen && (
                                            <div>
                                                {availableCounties
                                                    .filter((c) => c.name.toLowerCase().includes(dropdownSearch.toLowerCase()))
                                                    .map((c, i) => (
                                                        <div
                                                            key={c.id}
                                                            className="select-item"
                                                            onMouseDown={(e) => { e.preventDefault(); addCounty(c); }}
                                                        >
                                                            {c.name}
                                                        </div>
                                                    ))}
                                                {availableCounties.filter((c) => c.name.toLowerCase().includes(dropdownSearch.toLowerCase())).length === 0 && (
                                                    <div className="lh-align-center">No Counties Found</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={selectedCounties.length > 0 || dropdownOpen ? 'focused' : 'unfocused'}>Counties</div>
                    </div>
                </FormRow>
                <DialogActionBar>
                    <Button
                        intent="primary"
                        type="submit"
                        text="Save"
                    />
                    <Button intent="danger" onClick={props.onCloseDialog} text="Cancel" />
                </DialogActionBar>
            </form>
        </Dialog>
    );
};

export default AddUpdateROWLineDialog;


// WEBPACK FOOTER //
// ./src/components/project/sections/tract/dialogs/AddUpdateROWLineDialog.tsx